import React, { useState } from 'react';
import { reportService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { SignaturePad } from '../components/SignaturePad';

export const ReportPage: React.FC = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    reportType: 'DAR' as 'DAR' | 'weekly' | 'monthly',
    siteId: '',
    description: '',
  });
  
  const [signature, setSignature] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleReset = () => {
    setFormData({ reportType: 'DAR', siteId: '', description: '' });
    setSignature(null);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await reportService.createReport({
        ...formData,
        photos: [],
        geoLocation: { latitude: 0, longitude: 0 },
      });
      setSuccess('Official report created successfully and submitted to headquarters.');
      setFormData({ reportType: 'DAR', siteId: '', description: '' });
      setSignature(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create report. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignatureCapture = (sig: string) => {
    setSignature(sig);
  };

  const isManager = ['super_admin', 'state_manager'].includes(user?.role || '');

  return (
    <div className="p-3 sm:p-6 max-w-4xl mx-auto font-sans min-w-0 max-w-full overflow-hidden">
      <div className="mb-6 sm:mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 flex items-center gap-2.5 sm:gap-3">
            <span>Official Reports</span>
            <button onClick={handleReset} className="p-1.5 sm:p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-full transition-colors border border-slate-700" title="Reset Form">
              <svg className="w-3.5 sm:w-4 h-3.5 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            </button>
          </h1>
          <p className="text-slate-400 text-sm">Submit daily, weekly, or monthly activity reports.</p>
        </div>
      </div>
      
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl flex items-center gap-3">
          <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          {error}
        </div>
      )}
      
      {success && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 text-green-400 rounded-xl flex items-center gap-3">
          <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 md:p-8 shadow-xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Report Type</label>
            <select
              name="reportType"
              value={formData.reportType}
              onChange={handleChange}
              className="w-full bg-slate-950 border border-slate-800 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-orange-500 transition-colors"
            >
              <option value="DAR">Daily Activity Report (DAR)</option>
              <option value="weekly">Weekly Summary</option>
              <option value="monthly">Monthly Overview</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Site / Location ID</label>
            <input
              type="text"
              name="siteId"
              value={formData.siteId}
              onChange={handleChange}
              placeholder="e.g. SITE-001 or WAREHOUSE-A"
              className="w-full bg-slate-950 border border-slate-800 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-orange-500 transition-colors placeholder:text-slate-600"
              required
            />
          </div>
        </div>

        <div className="mb-8">
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Detailed Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={6}
            placeholder="Describe the activities, incidents, or summaries for this period..."
            className="w-full bg-slate-950 border border-slate-800 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-orange-500 transition-colors placeholder:text-slate-600"
            required
          />
        </div>

        {isManager && (
          <div className="mb-8 border-t border-slate-800 pt-8">
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Manager Authorization Signature</label>
            <div className="bg-white rounded-xl overflow-hidden p-2 border-2 border-slate-700 focus-within:border-orange-500 transition-colors inline-block w-full max-w-lg">
              <SignaturePad onSignatureCapture={handleSignatureCapture} />
            </div>
            {!signature && <p className="text-xs text-amber-500 mt-2">Signature is required for manager level reports.</p>}
          </div>
        )}

        <div className="flex justify-end pt-4 border-t border-slate-800">
          <button
            type="submit"
            disabled={loading || (isManager && !signature)}
            className="bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 text-white font-bold px-8 py-3.5 rounded-xl transition shadow-lg shadow-orange-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                Submitting...
              </>
            ) : (
              'Submit Certified Report'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
